import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { __ } from '@wordpress/i18n';
import CptTable from './CptTable';
import { activityColumns } from './CptTable/columns/activities';
import { apiFetch } from '../scripts/api';
import {
	fetchAllActivities,
	fetchMyRegistrations,
	withUserRegistrations,
} from '../scripts/activitiesData';
import { downloadCsv } from '../scripts/exportCsv';

function formatDatetime( val ) {
	if ( ! val ) {
		return '';
	}
	const match = val.match( /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/ );
	if ( ! match ) {
		return val;
	}
	const [ , year, month, day, hour, min ] = match;
	return `${ day }/${ month }/${ year } ${ hour }:${ min }`;
}

const postTitle = ( c ) => c?.node?.title ?? '';

function exportActivitiesToCSV( rows ) {
	const headers = [
		__( 'Activity', 'anacrm' ),
		__( 'Project', 'anacrm' ),
		__( 'Organisation', 'anacrm' ),
		__( 'Start', 'anacrm' ),
		__( 'End', 'anacrm' ),
		__( 'Registered people', 'anacrm' ),
	];
	const dataRows = rows.map( ( r ) => {
		const org =
			r.orgRef?.node?.title ||
			r.project?.node?.orgRef?.nodes
				?.map( ( n ) => n.title )
				.join( '; ' ) ||
			'';
		return [
			r.title ?? '',
			postTitle( r.project ),
			org,
			formatDatetime( r.dateStart ),
			formatDatetime( r.dateEnd ),
			r.registrationCount ?? 0,
		];
	} );
	downloadCsv(
		headers,
		dataRows,
		`activities-${ new Date().toISOString().slice( 0, 10 ) }.csv`
	);
}
import ActivityRegistrationsPanel from './ActivityRegistrationsPanel';

export default function ActivityTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ shareUrl, setShareUrl ] = useState( null );
	const [ copied, setCopied ] = useState( false );

	const isStaff = !! window.anacrm?.isStaff;

	useEffect( () => {
		const fetchAll = isStaff
			? fetchAllActivities().then( ( nodes ) => ( {
					nodes,
					registrations: {},
			  } ) )
			: Promise.all( [
					fetchAllActivities(),
					fetchMyRegistrations(),
			  ] ).then( ( [ nodes, registrations ] ) => ( {
					nodes,
					registrations,
			  } ) );

		fetchAll
			.then( ( { nodes, registrations } ) => {
				const enriched = withUserRegistrations(
					nodes,
					registrations,
					setData
				);
				setData( enriched );
			} )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	const handleDelete = async ( databaseId ) => {
		try {
			await apiFetch( `/anacrm/v1/activity/${ databaseId }`, {
				method: 'DELETE',
			} );
			setData( ( prev ) =>
				prev.filter( ( item ) => item.databaseId !== databaseId )
			);
		} catch ( err ) {
			window.alert(
				__( 'Could not delete the activity:', 'anacrm' ) + err.message
			);
		}
	};

	useEffect( () => {
		const handler = () => setShareUrl( window.location.href );
		document.addEventListener( 'anacrm:share-activity', handler );
		return () =>
			document.removeEventListener( 'anacrm:share-activity', handler );
	}, [] );

	const handleCopyLink = useCallback( () => {
		navigator.clipboard.writeText( shareUrl ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 2000 );
		} );
	}, [ shareUrl ] );

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const columns = useMemo(
		() => activityColumns( { onDelete: handleDelete } ),
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	const filters = [
		{
			columnId: 'project',
			label: __( 'Project', 'anacrm' ),
			getTerms: ( row ) =>
				row.project?.node?.title ? [ row.project.node.title ] : [],
		},
	];

	const shareContainer = document.getElementById( 'anacrm-share-activity' );

	return (
		<>
			<CptTable
				columns={ columns }
				data={ data }
				isLoading={ loading }
				filters={ filters }
				exportEvent="anacrm:export-activity"
				onExportRows={ exportActivitiesToCSV }
				searchPlaceholder={ __( 'Search activities…', 'anacrm' ) }
				renderSubRow={
					isStaff
						? ( row ) => (
								<ActivityRegistrationsPanel
									activityId={ row.original.databaseId }
								/>
						  )
						: undefined
				}
				fitWidth
			/>
			{ shareUrl !== null &&
				shareContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<div className="flex flex-col gap-1 min-w-0">
							<strong>{ __( 'Share', 'anacrm' ) }</strong>
							<div className="flex items-center gap-3 flex-wrap">
								<span className="text-xs">
									{ __( 'Link to share:', 'anacrm' ) }{ ' ' }
									<a
										href={ shareUrl }
										className="underline break-all"
									>
										{ shareUrl }
									</a>
								</span>
								<button
									onClick={ handleCopyLink }
									className="shrink-0 px-3 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-xs"
								>
									{ copied
										? __( 'Copied!', 'anacrm' )
										: __( 'Copy link', 'anacrm' ) }
								</button>
							</div>
						</div>
						<button
							onClick={ () => {
								setShareUrl( null );
								setCopied( false );
							} }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer shrink-0"
						>
							&times;
						</button>
					</div>,
					shareContainer
				) }
		</>
	);
}
